import React, {memo, useMemo} from 'react';
import cx from 'classnames';

/* Components */
import VoltranFragment from '../VoltranFragment/VoltranFragment';
import HbBreadCrumbView from '../Breadcrumb/HbBreadCrumbView';
import HbPageTitle from '../page/title/HbPageTitle';
import TotalProductCount from '../TotalProductCount';
import MoriaComponentsHelper from '../../modules/moriaComponents/moriaComponentsHelper';

/* Constants */
import {MORIA_COMPONENTS} from '../../modules/moriaComponents/moriaComponentsConstants';
import {DAVINCI_PAGE_TYPES} from '../../modules/analytics/davinci/davinciConstants';
import {DESKTOP_COMPONENTS, DESKTOP_COMPONENTS_OPTIONS} from '../../modules/voltran/voltranConstants';
import {PAGE_TITLE_SIZES} from '../page/title/HbPageTitleConstants';
import {BREADCRUMB_SIZES} from '../Breadcrumb/HbBreadbrumbConstants';
import Feedback from '../../partials/Feedback/Feedback';

import s from './MoriaComponentsView.scss';

const MoriaComponents = memo(({
  appliedFacetArray,
  dispatch,
  displayFeedback,
  displaySponsoredBrandComponent,
  isMobile,
  isSticky,
  moriaComponents,
  videoAdExists,
  pageType,
  pageValue,
  queryParams,
  scriptRenderMethodClientSide,
  sponsoredBrandFragment,
  showPLPRedesignLayout,
  breadCrumbConfigArray,
  seoInfo,
  titleElement,
  isRequestDispatcherEnable = true,
  lastItemDisabled
}) => {
  const isProductExist = moriaComponents?.[MORIA_COMPONENTS.PRODUCT_LIST]?.seoState?.totalProductCount > 0;
  const hasSearchResultComponent = moriaComponents?.[MORIA_COMPONENTS.SEARCH_RESULT_SUMMARY];
  const isSearchPage = pageType === DAVINCI_PAGE_TYPES.SEARCH;
  const isCategoryPage = pageType === DAVINCI_PAGE_TYPES.CATEGORY;
  const isTagPage = pageType === DAVINCI_PAGE_TYPES.TAG;
  const isMerchantPage = pageType === DAVINCI_PAGE_TYPES.MERCHANT;
  const isBrandPage = pageType === DAVINCI_PAGE_TYPES.BRAND;
  const totalProductCount = useMemo(() => MoriaComponentsHelper.getTotalProductCount(moriaComponents), [moriaComponents]);

  return (
    <div className={cx(s.root, {[s.newLayoutRoot]: showPLPRedesignLayout})} id='containerSelectorFocus'>
      <div className={s.container}>
        {(isProductExist || isMerchantPage) && (
          <div className={cx(s.verticalFilter, {[s.sticky]: isSticky})}>
            <VoltranFragment
              fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.VERTICAL_FILTER]}
              renderMethodClientSide={scriptRenderMethodClientSide}/>
          </div>
        )}

        <div className={s.productListContainer}>
          {
            videoAdExists ? (
              <div className={s.videoAdx}>
                <VoltranFragment
                  fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.GOOGLE_VIDEO_ADX]}
                  renderMethodClientSide={scriptRenderMethodClientSide}/>
              </div>
            ) :
              sponsoredBrandFragment && displaySponsoredBrandComponent && (
                <div className={s.sponsoredBrand}>
                  <VoltranFragment
                    fragmentInfo={sponsoredBrandFragment}
                    options={DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.SPONSORED_BRAND_COMPONENT]}/>
                </div>
              )
          }
          {
            isProductExist && isSearchPage && hasSearchResultComponent && (
              <div className={s.searchResultSummaryComponents}>
                <div className={s.searchResultSummary}>
                  <VoltranFragment
                    fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.SEARCH_RESULT_SUMMARY]}
                    renderMethodClientSide={scriptRenderMethodClientSide}/>
                </div>
                <div>
                  <VoltranFragment
                    fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.SORTING_BOX]}
                    renderMethodClientSide={scriptRenderMethodClientSide}/>
                </div>
              </div>
            )
          }
          {
            showPLPRedesignLayout && isProductExist && (isCategoryPage || isTagPage) && (
              <div className={s.newLayoutTitleArea}>
                <div className={s.newLayoutBreadcrumbs}>
                  {
                    breadCrumbConfigArray?.length > 0 &&
                    <HbBreadCrumbView
                      className={s.newLayoutBreadcrumb}
                      rootIconEnabled={false}
                      size={BREADCRUMB_SIZES.SMALL}
                      isSchemaStructuredActive
                      showPLPRedesignLayout
                      forceRedirect
                      lastItemDisabled={lastItemDisabled}
                      itemConfigs={breadCrumbConfigArray}/>
                  }
                </div>
                <div className={s.titleArea}>
                  <div className={s.newLayoutTitleContainer}>
                    <HbPageTitle
                      className={s.newLayoutTitle} title={seoInfo?.h1} size={PAGE_TITLE_SIZES.LARGE}
                      TitleElement={titleElement}/>
                    <TotalProductCount totalProductCount={totalProductCount} />
                  </div>
                  <div className={s.sortingButton}>
                    <VoltranFragment
                      fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.SORTING_BOX]}
                      renderMethodClientSide={scriptRenderMethodClientSide}/>
                  </div>
                </div>
              </div>
            )
          }
          {
            (isMerchantPage || isBrandPage) && (
              <div className={s.sortingBoxContainer}>
                <div className={s.sortingBox}>
                  <VoltranFragment
                    fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.SORTING_BOX]}
                    renderMethodClientSide={scriptRenderMethodClientSide}/>
                </div>
              </div>
            )
          }
          {isProductExist && !showPLPRedesignLayout && moriaComponents?.[MORIA_COMPONENTS.JET_DELIVERY] && (
            <div className={s.jetDelivery}>
              <VoltranFragment
                fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.JET_DELIVERY]}
                renderMethodClientSide={scriptRenderMethodClientSide}/>
            </div>
          )}

          {isProductExist && moriaComponents?.[MORIA_COMPONENTS.HERO_FILTER] && (
            <div className={s.heroFilter}>
              <VoltranFragment
                fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.HERO_FILTER]}
                renderMethodClientSide={scriptRenderMethodClientSide}/>
            </div>
          )}

          <div className={s.productListWrapper}>
            <VoltranFragment
              fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.PRODUCT_LIST]}
              renderMethodClientSide={scriptRenderMethodClientSide}/>
          </div>
          {
            isSearchPage && isProductExist && displayFeedback && (
              <Feedback
                dispatch={dispatch}
                queryParams={queryParams}
                appliedFacetArray={appliedFacetArray}
                isMobile={isMobile}
                pageTypeForDavinci={DAVINCI_PAGE_TYPES.SEARCH}
                pageValueForDavinci={pageValue}/>
            )}
        </div>

        {isRequestDispatcherEnable && (
          <VoltranFragment
            fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.REQUEST_DISPATCHER]}
            renderMethodClientSide={scriptRenderMethodClientSide}/>
        )}
      </div>
    </div>
  )
});

export default MoriaComponents;
